import { Drawer, useTheme } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { MainLinks } from 'src/feature/header/component/original/organism/MainLinks'
import { ControlPanelSidebar } from 'src/feature/control-panel/component/ControlPanelSidebar'
import { useUserStore } from 'src/feature/user/store/user.store'
import { MyTeamSidebar } from 'src/feature/my-team/component/organisms/MyTeamSidebar'
import { useAuthStore } from 'src/feature/auth/store/auth.store'
import { OrganisationRole } from 'src/feature/control-panel/model/OrganisationRole'
import { ELearningSidebar } from 'src/feature/e-learning/component/organism/ELearningSidebar'
import YohrsMainLogo from 'src/feature/image/asset/logo/yohrs-main-logo.png'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'
import { ProjectBuilderSidebar } from 'src/feature/project-builder/component/organisms/ProjectBuilderSidebar'

export type WithDrawerlayoutProps = {
  drawerWidth: number
  isOpen: boolean
}

export const WithDrawerlayout = observer(({ drawerWidth, isOpen }: WithDrawerlayoutProps) => {
  const theme = useTheme()
  const userStore = useUserStore()
  const authStore = useAuthStore()

  return (
    <Drawer
      open={isOpen}
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: 'none',
          border: 'none',
          borderRadius: `0 16px 16px 0`,
          backgroundColor: Palette.DEEP_SEA_BLUE,
          color: Palette.WHITE,
          padding: '1.5em',
          height: '100%',
          alignItems: isOpen ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
          overflowX: 'hidden',
          '& .MuiSvgIcon-root': { color: Palette.WHITE },
          '& .MuiTypography-root, & p': { color: Palette.WHITE },
          '& .MuiListItemText-root': { color: Palette.WHITE },
          '& .MuiListItem-root:hover .link-label': { color: ColorTokens.primary200 },
          '& .MuiListItem-root.Mui-selected .link-label': { color: ColorTokens.primary200, textDecoration: 'underline' },
        },
      }}
    >
      <AppBox
        sx={{
          flexDirection: 'column',
          gap: theme.spacing(10),
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {isOpen && (
          <AppBox sx={styles.logoWrap}>
            <AppBox
              component="img"
              src={YohrsMainLogo}
              alt="Yohrs"
              sx={styles.logoImage}
            />
          </AppBox>
        )}
        <MainLinks isOpen={isOpen} />
        {(userStore.sitemap?.modules ?? []).some(s => s.key === 'eLearningV2') && <ELearningSidebar isOpen={isOpen} />}
        {authStore.hasRole(OrganisationRole.UserAdministrator) && (userStore.sitemap?.modules ?? []).some(s => s.key === 'skillGap') && <MyTeamSidebar isOpen={isOpen} />}
        {authStore.hasRole(OrganisationRole.UserAdministrator) && (userStore.sitemap?.modules ?? []).some(s => s.key === 'projectBuilder') && <ProjectBuilderSidebar isOpen={isOpen} />}
        {(userStore.sitemap?.modules ?? []).filter(s => s.key === 'controlpanel' && s.level === 'admin').length > 0 && <ControlPanelSidebar isOpen={isOpen} />}
      </AppBox>
    </Drawer>
  )
})

const styles = {
  logoWrap: {
    width: '100%',
    justifyContent: 'center',
    mb: 8,
  },
  logoImage: {
    width: '100%',
    maxWidth: 180,
    height: 'auto',
    objectFit: 'contain',
  },
}